import { ApiError } from './api';

/**
 * The server's "this looks like one you just posted" response.
 *
 * Keyed on `code` rather than the message text so the wording can change
 * without silently turning the guard off.
 */
export interface PossibleDuplicateBody {
  error: string;
  code: 'possible_duplicate';
  entityType: string;
  existingId: string;
}

export function asPossibleDuplicate(err: unknown): PossibleDuplicateBody | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as PossibleDuplicateBody | null;
  return body?.code === 'possible_duplicate' ? body : null;
}

/**
 * Run a write, and if the server flags a possible duplicate, ask once and retry
 * with the override.
 *
 * The confirmation is deliberately a plain browser confirm: it must be
 * impossible to dismiss by accident, and it must interrupt. The alternative —
 * an inline banner — is exactly the kind of thing you click past when you are
 * moving quickly, which is when duplicates get posted in the first place.
 *
 * Returns null if the user declines, so callers can distinguish "cancelled"
 * from "succeeded".
 */
export async function withDuplicateConfirm<T, I extends { allowDuplicate?: boolean }>(
  input: I,
  submit: (input: I) => Promise<T>,
): Promise<T | null> {
  try {
    return await submit(input);
  } catch (err) {
    const dup = asPossibleDuplicate(err);
    if (!dup) throw err;
    if (!window.confirm(`${dup.error}\n\nOK to post it anyway, Cancel to stop.`)) return null;
    return submit({ ...input, allowDuplicate: true });
  }
}
